import React, { useState, useEffect } from 'react';
import { Card, Button, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { renderShopLogo } from '../../utils/imageHelpers';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { getShop, convertShopNameToUrl } from '../../services/shopService';

const CheckoutPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cartItems, loading: cartLoading, error: cartError } = useCart();
  const { currentUser } = useAuth();
  const [shopsData, setShopsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Carica i dati dei negozi
  useEffect(() => {
    const loadShopsData = async () => {
      try {
        setLoading(true);
        // Raggruppa gli shopId unici dal carrello
        const uniqueShopIds = [...new Set(cartItems.map(item => item.shopId))];
        
        // Carica i dati per ogni negozio
        const shopsDataPromises = uniqueShopIds.map(async (shopId) => {
          if (!shopId) {
            console.error('ShopId non valido:', shopId);
            return [shopId, null];
          }
          try {
            const shopData = await getShop(shopId);
            return [shopId, shopData];
          } catch (err) {
            console.error(`Errore nel caricamento del negozio ${shopId}:`, err);
            return [shopId, null];
          }
        });

        const shopsDataArray = await Promise.all(shopsDataPromises);
        const shopsDataObject = Object.fromEntries(shopsDataArray.filter(([_, data]) => data !== null));
        setShopsData(shopsDataObject);
      } catch (err) {
        console.error('Errore nel caricamento dei dati dei negozi:', err);
        setError('Errore nel caricamento dei dati dei negozi');
      } finally {
        setLoading(false);
      }
    };

    if (cartItems.length > 0) {
      loadShopsData();
    } else {
      setLoading(false);
    }
  }, [cartItems]);

  // Raggruppa gli elementi del carrello per negozio
  const itemsByShop = cartItems.reduce((acc, item) => {
    if (!acc[item.shopId]) {
      acc[item.shopId] = {
        shopId: item.shopId,
        items: []
      };
    }
    acc[item.shopId].items.push(item);
    return acc;
  }, {});

  // Calcola il costo di spedizione per un negozio
  const getShippingCost = (shopData, subtotal) => {
    const shipping = shopData?.shipping;
    if (!shipping) return 0;
    const cost = typeof shipping.cost === 'number' ? shipping.cost : 0;
    if (cost === 0) return 0;
    const threshold = parseFloat(shipping.freeThreshold) || 0;
    if (threshold > 0 && subtotal >= threshold) return 0;
    return cost;
  };

  // Calcola il totale per shop (inclusa spedizione secondo le impostazioni del negozio)
  const calculateShopTotal = (items, shopData) => {
    const subtotal = items.reduce((total, item) =>
      total + (item.isAvailable !== false ? item.price * item.quantity : 0), 0
    );
    const shippingCost = getShippingCost(shopData, subtotal);
    return { subtotal, shippingCost, total: subtotal + shippingCost };
  };

  // Calcola il numero totale di articoli per shop (escludendo quelli non disponibili)
  const countItems = (items) => {
    return items.reduce((count, item) => 
      count + (item.isAvailable !== false ? item.quantity : 0), 0
    );
  };

  if (loading || cartLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  if (error || cartError) {
    return <div className="text-center py-5 text-danger">{error || cartError}</div>;
  }

  if (!currentUser) {
    return (
      <div className="text-center py-5">
        <p>Devi effettuare il login per vedere il carrello</p>
        <Button variant="primary" className="rounded-pill" onClick={() => navigate('/login')}>
          Accedi
        </Button>
      </div>
    );
  }

  // Carrello vuoto: mostra icona e messaggio
  if (cartItems.length === 0) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
        <i className="bi bi-cart-x" style={{ fontSize: 80, color: '#e0e0e0' }}></i>
        <div className="mt-3 text-muted" style={{ fontSize: 20 }}>{t('checkout.empty_cart') || 'Il carrello è vuoto'}</div>
        <Button 
          variant="primary" 
          className="mt-3"
          onClick={() => navigate('/shops')}
        >
          Continua lo shopping
        </Button>
      </div>
    );
  }

  return (
    <div className="py-2">
      <h4 className="mb-4 fw-bold">{t('checkout.your_cart')}</h4>
      
      {Object.values(itemsByShop).map((shop) => {
        const shopData = shopsData[shop.shopId];
        if (!shopData) {
          console.error('Dati del negozio non trovati per shopId:', shop.shopId);
          return null;
        }

        const availableItems = shop.items.filter(item => item.isAvailable !== false);
        const totalItems = countItems(shop.items);
        const { shippingCost, total } = calculateShopTotal(shop.items, shopData);

        return (
          <Card
            key={shop.shopId}
            className="mb-4 rounded-4 shop-card"
            style={{ cursor: 'pointer' }}
            onClick={() => navigate(`/checkout/${convertShopNameToUrl(shopData.name)}`)}
          >
            <Card.Body className="p-3">
              <div className="d-flex align-items-center">
                <div className="rounded-circle overflow-hidden bg-white d-flex align-items-center justify-content-center" style={{ width: 60, height: 60 }}>
                  {renderShopLogo(shopData.logoUrl, shopData.name, 60)}
                </div>
                <div className="ms-3 flex-grow-1">
                  <h5 className="mb-0 fw-bold text-dark">{shopData.name}</h5>
                  <div className="d-flex justify-content-between align-items-center">
                    <p className="text-muted small mb-0">
                      {totalItems} {t('checkout.items')}
                      {availableItems.length < shop.items.length && (
                        <span className="text-danger ms-2">
                          ({shop.items.length - availableItems.length} non disponibili)
                        </span>
                      )}
                    </p>
                    <div className="text-end">
                    <p className="fw-bold mb-0 text-dark">
                        €{total.toFixed(2)}
                    </p>
                      <small className="text-muted">
                        {shippingCost === 0 ? 'spedizione gratuita' : `incl. sped. €${shippingCost.toFixed(2)}`}
                      </small>
                    </div>
                  </div>
                </div>
                <i className="bi bi-chevron-right text-muted ms-2"></i>
              </div>
            </Card.Body>
          </Card>
        );
      })}
    </div>
  );
};

export default CheckoutPage; 